/* eslint-disable comma-dangle */

import axios from 'axios';
// import 'core-js/es';

import probeConnection, {
  SCIConnection, timedGet, logError, SCIAlreadyClaimedError
} from './ec9xns-connection';

import {
  compareSemanticVersion, createUpdateObjectV1, createUpdateObjectV2,
  createDashboardUpdateObjectV2, createManualUpdateObject
} from './ec9xns-update';

import {FirmwareNotCompatibleError, ScannerNotConnectedError, ScannerFirmwareUpdateError} from './ec9xns-error';

function getScannerInfo(xDevice, options = {}) {

  function createConnection() {
    if (options.isDeviceURL) {
      return Promise.resolve(new SCIConnection(xDevice));
    } else {
      return probeConnection(xDevice, { isDeviceURL: options.isDeviceURL }).then((cUrl) => {
        return new SCIConnection(cUrl);
      });
    }
  }

  const scannerUserID = options.scannerUserID || `SFU-${new Date().getTime()}`;
  let conn = null;
  return createConnection().then((xconn) => {
    conn = xconn;
    /*Reclaim scanner is required*/
    return conn.claimScanner(scannerUserID);
  }).catch((err) => {
    if (err instanceof SCIAlreadyClaimedError) {
      return Promise.reject(err);
    }
    return Promise.reject(new ScannerNotConnectedError('Cannot connect to scanner.'));
  }).then(
    () => conn.getScannerXml()
  ).then((resp) => {
    const docDom = resp[0];
    const scannerURL = conn.scannerURL;

    if (!options.scannerUserID) {
      /* Only need to release scanner when scanner session is created by us */
      conn.releaseScanner();
      conn = null;
    }

    let [deviceSN, deviceType] = ['MetaSerialNumber', 'MetaScannerType'].map((elemName) => {
      const elems = docDom.getElementsByTagName(elemName);
      return elems.length > 0 ? elems[0].textContent : null;
    });

    if (!deviceType || !deviceSN) {
      return Promise.reject(new FirmwareNotCompatibleError('An old firmware is detected - SN/Type not found'));
    }

    let versions = [...docDom.getElementsByTagName('VersionData')].reduce((result, versionDataElem) => {
      const nameElems = versionDataElem.getElementsByTagName('Name');
      const versionElems = versionDataElem.getElementsByTagName('Version');

      if (nameElems.length > 0 && versionElems.length > 0) {
        result[nameElems[0].textContent] = versionElems[0].textContent;
      }
      return result;
    }, {});

    if ('Firmware' in versions && 'PCB ID' in versions) {
      return {
        deviceSN: deviceSN,
        deviceType: deviceType,
        deviceURL: scannerURL,
        versions
      };
    } else if ('librcp' in versions && 'PCB ID' in versions) {
      versions['Firmware'] = versions['librcp'];
      return {
        deviceSN: deviceSN,
        deviceType: deviceType,
        deviceURL: scannerURL,
        versions
      };
    }

    return Promise.reject(new FirmwareNotCompatibleError('An old firmware is detected - Firmware version not found'));
  }).catch((cErr) => {
    logError(cErr);
    if (conn && !(cErr instanceof SCIAlreadyClaimedError)) {
      try {
        conn.releaseScanner(scannerUserID);
      } catch (e) {
        logError(e);
      }
    }
    return Promise.reject(cErr);
  });
}

function getDeviceCertPkgPath(xSN) {
  function zeroPad(num, places) {
    var zero = places - num.toString().length + 1;
    return Array(+(zero > 0 && zero)).join('0') + num;
  }

  let folders = ['other', null];

  xSN = xSN || '';
  switch (xSN.length) {
    case 13:
    case 15: {
      let days = parseInt(xSN.substring(0, 6));
      let seq = parseInt(xSN.substring(xSN.length - 3));
      if (days && seq) {
        folders[0] = days % 1000;
        folders[1] = (seq) % 1000;
      }
      break;
    }

    default:
      break;
  }

  return folders.reduce((path, elem) => (elem ? (path + '/' + zeroPad(elem, 3)) : path), '');
}

/**
 * Carga y parsea el XML de updates para el device indicado.
 * - EC9600: conserva lógica legacy vs Update2.xml
 * - EC9700: intenta Update2.xml, si falla prueba Updates2.xml
 */
async function getAvailableUpdates(xUpdateURL, xDeviceType, xDeviceSN, xFwVersions) {
  let urlRoot = null;
  let updateXML = null;
  xFwVersions = xFwVersions || {};

  switch (xDeviceType.toUpperCase()) {
    case 'EC9600':
      urlRoot = `${xUpdateURL}/EC9600`;
      if (('Firmware' in xFwVersions) && (compareSemanticVersion(xFwVersions['Firmware'], '1.2.1.283', { zeroFill: true, treatBuildAsNumber: true }) <= 0)) {
        // legacy update.xml
        updateXML = `${urlRoot}/Update.xml`;
      } else {
        updateXML = `${urlRoot}/Update2.xml`;
      }
      break;

    case 'EC9700':
    case 'EC9700I': {
      urlRoot = `${xUpdateURL}/EC9700`;

      // Fallback: algunos repos usan Update2.xml, otros Updates2.xml
      const candidates = [
        `${urlRoot}/Update2.xml`,
        `${urlRoot}/Updates2.xml`,
      ];

      let chosen = candidates[0];
      for (const u of candidates) {
        try {
          const r = await axios.get(u, { timeout: 7000, responseType: 'text', params: { t: Date.now() } });
          if (r && r.status >= 200 && r.status < 300) {
            chosen = u;
            break;
          }
        } catch (_e) {
          // continúa probando el siguiente
        }
      }
      updateXML = chosen;
      break;
    }

    default:
      return Promise.resolve([null, null, null]);
  }

  // Descarga del XML (con cache-buster)
  return axios.get(updateXML, {
    params: { t: new Date().getTime() },
    responseType: 'text'
  }).then((cUpdateServerResponse) => {
    if (cUpdateServerResponse.status !== 200) {
      return Promise.reject(Error(cUpdateServerResponse.status));
    }
    return cUpdateServerResponse.data;
  }).catch((e) => {
    logError(e);
    return Promise.reject(new ScannerFirmwareUpdateError(`Cannot connect to update server - ${e.message}`));
  }).then((cText) => {
    const updateDoc = new DOMParser().parseFromString(cText, 'application/xml');

    // ---- FIRMWARE LIST ----
    let fwAvailables = [...updateDoc.getElementsByTagName('Firmware')].map((firmwareElem) => {
      const filePath = firmwareElem.getElementsByTagName('FilePath')[0].textContent.trim();
      const licNode = firmwareElem.getElementsByTagName('LicensePath')[0];
      const licPath = licNode ? licNode.textContent.trim() : null;
      const detailsNode = firmwareElem.getElementsByTagName('Details')[0];
      const details = detailsNode ? detailsNode.textContent : '';

      return {
        type: 'Firmware',
        displayName: firmwareElem.getAttribute('DisplayName'),
        version: firmwareElem.getAttribute('Version'), // p.ej. "1.3.1-0069"
        applicablePCB: firmwareElem.hasAttribute('PCBCfg') ? firmwareElem.getAttribute('PCBCfg') : null,
        required: firmwareElem.hasAttribute('Forced') ? (firmwareElem.getAttribute('Forced').toLowerCase() === 'true') : false,
        requiredDate: firmwareElem.hasAttribute('RequiredDate') ? firmwareElem.getAttribute('RequiredDate') : null,

        pkgURL: `${urlRoot}/${filePath}`,
        licenseUrl: licPath ? `${urlRoot}/${licPath}` : null,
        details
      };
    });

    // Para EC9700 garantizamos que la primera entrada sea la versión más reciente
    if (/^EC9700/i.test(xDeviceType)) {
      fwAvailables.sort((a, b) =>
        compareSemanticVersion(b.version, a.version, { zeroFill: true, treatBuildAsNumber: true })
      );
      // Si tu flujo espera una sola opción, deja solo la más nueva.
      if (fwAvailables.length > 1) {
        fwAvailables = fwAvailables.slice(0, 1);
      }
    }

    // ---- OMNI CERTS ----
    const certsUpdates = [...updateDoc.getElementsByTagName('OmniCertificate')].map((certElem) => {
      const filePath = certElem.getElementsByTagName('FilePath')[0].textContent.trim();
      const licNode = certElem.getElementsByTagName('LicensePath')[0];
      const licPath = licNode ? licNode.textContent.trim() : null;
      const detailsNode = certElem.getElementsByTagName('Details')[0];
      const details = detailsNode ? detailsNode.textContent : '';

      return {
        type: 'OMNI Certificates',
        displayName: certElem.getAttribute('DisplayName'),
        version: certElem.getAttribute('Version'),
        applicablePCB: certElem.hasAttribute('PCBCfg') ? certElem.getAttribute('PCBCfg') : null,
        required: certElem.hasAttribute('Forced') ? (certElem.getAttribute('Forced').toLowerCase() === 'true') : false,
        requiredDate: certElem.hasAttribute('RequiredDate') ? certElem.getAttribute('RequiredDate') : null,

        pkgURL: `${urlRoot}/${filePath}`,
        licenseUrl: licPath ? `${urlRoot}/${licPath}` : null,
        details
      };
    });

    // ---- DEVICE CERT (por SN) ----
    const deviceCertAvailables = [...updateDoc.getElementsByTagName('DeviceCertificate')].map((certElem) => {
      const basePath = certElem.getElementsByTagName('BasePath')[0].textContent.trim();
      const detailsNode = certElem.getElementsByTagName('Details')[0];
      const details = detailsNode ? detailsNode.textContent : '';
      const updatePath = `${urlRoot}/${basePath}/${getDeviceCertPkgPath(xDeviceSN)}/${xDeviceSN}.bin`;
      return axios.get(updatePath, {
        params: { t: new Date().getTime() }
      }).then(() => {
        const licNode = certElem.getElementsByTagName('LicensePath')[0];
        const licPath = licNode ? licNode.textContent.trim() : null;
        return {
          type: 'Device Update',
          displayName: certElem.getAttribute('DisplayName'),
          version: certElem.getAttribute('Version'),
          required: certElem.hasAttribute('Forced') ? (certElem.getAttribute('Forced').toLowerCase() === 'true') : false,
          requiredDate: certElem.hasAttribute('RequiredDate') ? certElem.getAttribute('RequiredDate') : null,
          applicablePCB: certElem.hasAttribute('PCBCfg') ? certElem.getAttribute('PCBCfg') : null,

          pkgURL: updatePath,
          licenseUrl: licPath ? `${urlRoot}/${licPath}` : null,
          details
        };
      }).catch(() => null);
    });

    return Promise.all([fwAvailables, certsUpdates, Promise.all(deviceCertAvailables)]);
  }).catch((e) => {
    logError(e);
    if (e instanceof ScannerFirmwareUpdateError) {
      return Promise.reject(e);
    } else {
      return Promise.reject(new ScannerFirmwareUpdateError(`Cannot load updates: ${e.message}`));
    }
  });
}

function getLicenseOfUpdate(xUpdate) {
  if (xUpdate && xUpdate.licenseUrl) {
    return timedGet(xUpdate.licenseUrl, { responseType: 'text' }).then((cResponse) => {
      if (cResponse.status !== 200) {
        return '';
      }
      // Axios: payload en data
      return (typeof cResponse.data === 'string') ? cResponse.data : '';
    }).catch(() => '');
  } else {
    return Promise.resolve('');
  }
}

function getValidCurVersion(version) {
  if (version === undefined || version === null || !(/^\d/.test(version))) {
    return '0.0.0';
  }
  return (version);
}

/**
 *
 * @param {*} deviceSN
 * @param {*} updateServer
 */
function checkScannerUpdates(xDevice = null, xOptions = {}) {
  let deviceInfo = null;

  // START HERE — default a PROD-2025
  const updateURL = (
    xOptions && xOptions.updateURL && xOptions.updateURL !== 'rdm://default'
      ? xOptions.updateURL.replace('rdm://', 'https://')
      : 'https://testscannerupdate.rdmcorp.com/PROD-2025'
  ).replace(/\/+$/, '');
  // ENDS HERE

  const legacyMode = ('checkingMode' in xOptions && xOptions.checkingMode.toUpperCase() === 'LEGACY');
  const dashboardMode = ('checkingMode' in xOptions && xOptions.checkingMode.toUpperCase() === 'DASHBOARD');

  return getScannerInfo(xDevice, { isDeviceURL: xOptions.isDeviceURL || dashboardMode, scannerUserID: xOptions.scannerUserID }).then((cDeviceInfo) => {
    deviceInfo = cDeviceInfo;
    return getAvailableUpdates(updateURL, cDeviceInfo.deviceType, cDeviceInfo.deviceSN, cDeviceInfo.versions);
  }).then(([fwAvailables, omniCertAvailables, deviceCertAvailables]) => {
    fwAvailables = fwAvailables || [];
    omniCertAvailables = omniCertAvailables || [];
    deviceCertAvailables = deviceCertAvailables || [];

    const pcbID = deviceInfo.versions['PCB ID'].replace(/^0+/, '');
    const curVersions = [
      getValidCurVersion(deviceInfo.versions['OMNI Certificate']),
      getValidCurVersion(deviceInfo.versions['Device Certificate']),
      deviceInfo.versions['Firmware'],
    ];

    const updateLists = [omniCertAvailables, deviceCertAvailables, fwAvailables].map(
      (lst, lstIdx) => {
        return lst.filter((updateItem) => {
          return updateItem
            && (!updateItem.applicablePCB || updateItem.applicablePCB.includes(`[${pcbID}]`))
            && (
              compareSemanticVersion(updateItem.version, curVersions[lstIdx], { zeroFill: true, treatBuildAsNumber: true }) > 0 ||
              (updateItem.required && compareSemanticVersion(updateItem.version, curVersions[lstIdx], { zeroFill: true, treatBuildAsNumber: true }) !== 0)
            );
        }).filter((updateItem, updateIndex) => {
          return updateItem && (dashboardMode || updateIndex == 0);
        }).map((updateItem) => ({
          type: updateItem.type,
          currentVersion: curVersions[lstIdx],
          availableVersion: updateItem.version,
          requiredDate: updateItem.requiredDate,
          displayName: updateItem.displayName,
          details: updateItem.details,
          licenseUrl: updateItem.licenseUrl,
          pkgURL: updateItem.pkgURL
        }));
      }
    );

    if (updateLists.every(lst => lst.length == 0)) {
      return null;
    }

    return Promise.all(updateLists.map((lst) => {
      //Only retrieve the license text for each update type;
      return (lst.length > 0) ? getLicenseOfUpdate(lst[0]) : null;
    })).then((licTextList) => {
      const [omniCertUpdates, deviceCertUpdates, fwUpdates] = updateLists.map(
        (lst, index) => lst.map((item) => {
          return { ...item, licenseHtml: licTextList[index] || '' };
        })
      );

      if (legacyMode) {
        return createUpdateObjectV1(
          deviceInfo,
          [
            ...omniCertUpdates.slice(0, 1),
            ...deviceCertUpdates.slice(0, 1),
            ...fwUpdates.slice(0, 1)
          ].filter(entry => entry !== null)
        );
      } else if (dashboardMode) {
        return createDashboardUpdateObjectV2(
          deviceInfo,
          [
            ...omniCertUpdates,
            ...deviceCertUpdates,
            ...fwUpdates
          ].filter(entry => entry !== null)
        );
      } else {
        return createUpdateObjectV2(
          deviceInfo,
          [
            ...omniCertUpdates.slice(0, 1),
            ...deviceCertUpdates.slice(0, 1),
            ...fwUpdates.slice(0, 1)
          ].filter(entry => entry !== null)
        );
      }
    }).catch((e) => {
      logError(e);
      return Promise.reject(new Error(`Failed retrieving license text! - ${e.message}`));
    });
  }).catch((e) => {
    if (e instanceof SCIAlreadyClaimedError) {
      logError(e);
      return Promise.reject(e);
    }

    if (e instanceof FirmwareNotCompatibleError) {
      return Promise.reject(e);
    }

    logError(e);
    return Promise.reject(e);
  });
}

function checkFirmwareUpdate(xDevice = null, xUpdateURL = null) {
  return checkScannerUpdates(xDevice, {
    updateURL: xUpdateURL,
    // checkingMode: 'legacy'
    // checkingMode: 'dashboard'
  });
}

function makeFileUpdate(xDeviceURL, fileObj) {
  return createManualUpdateObject(xDeviceURL, fileObj);
}

function probeConnection2(xDevice, xOption) {
  return probeConnection(xDevice, xOption).then(function (cUrl) {
    return new SCIConnection(cUrl);
  });
}

// const MODU_LABEL = 'EC9600 SFU';

export {
  checkFirmwareUpdate, checkScannerUpdates, makeFileUpdate, probeConnection, probeConnection2
};

export * from './ec9xns-error';
